import { motion } from "framer-motion";
import { Sparkles, Crown, Lock } from "lucide-react";
import { useAuth } from "../App";

/**
 * FoundingMemberBanner — dashboard hero badge that announces the locked-rate
 * privilege for beta / founding / lifetime-free members.
 *
 * Visibility rules:
 *  - member_type === "beta" + is_beta_founding_member  → plum "Beta Founding Member"
 *  - member_type === "founding" OR is_founding_member  → sage  "Founding Member"
 *  - member_type === "lifetime_free"                   → gold  "Lifetime Member"
 *  - otherwise                                         → null (renders nothing)
 *
 * Standard members see no banner.
 */
const FoundingMemberBanner = () => {
  const { user } = useAuth();
  if (!user) return null;

  const mt = user.member_type;
  const isLifetime = mt === "lifetime_free" || user.is_lifetime_free;
  const isFounding = mt === "founding" || user.is_founding_member;
  const isBetaFounding = mt === "beta" && user.is_beta_founding_member;

  if (!isLifetime && !isFounding && !isBetaFounding) return null;

  let palette, Icon, label, sub;
  if (isLifetime) {
    palette = { bg: "from-[#D89B4E] via-[#B8842F] to-[#94652D]", iconBg: "#FFFBEB", iconColor: "#D97706" };
    Icon = Crown;
    label = "Lifetime Member";
    sub = "Permanent full RDH Pro access — no trial, no subscription, ever.";
  } else if (isBetaFounding) {
    palette = { bg: "from-[#6B4E8A] via-[#553D6F] to-[#3E2B52]", iconBg: "#F0ECF7", iconColor: "#6B4E8A" };
    Icon = Sparkles;
    label = "Founding Beta Member — Rate Locked for Life";
    sub = "Your tier rate is permanently locked at beta founding pricing. No future price increase will ever apply to you.";
  } else {
    palette = { bg: "from-[#8DB5A0] via-[#6FA08A] to-[#558B72]", iconBg: "#ECFDF5", iconColor: "#10B981" };
    Icon = Lock;
    label = "Founding Member — Rate Locked for Life";
    sub = "You're one of the first 100 founding members. Your tier rate is permanently locked at founding pricing.";
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${palette.bg} text-white p-4 sm:p-5 mb-6 shadow-sm`}
      data-testid="founding-member-banner"
      role="status"
    >
      {/* Decorative shimmer */}
      <div className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.3), transparent 50%)"
        }}
      />
      <div className="relative flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: palette.iconBg }}
        >
          <Icon className="w-5 h-5" style={{ color: palette.iconColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="font-medium text-sm sm:text-base leading-tight mb-1"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="founding-banner-label"
          >
            {label}
          </p>
          <p className="text-xs text-white/85 leading-relaxed">
            {sub}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default FoundingMemberBanner;
